import { forwardRef, useCallback } from "react";
import { Input } from "@/components/ui/input";

/**
 * MoneyInput — muestra el valor con separadores de miles en vivo (es-CO).
 *
 * Uso:
 *   <MoneyInput value={precio} onValueChange={setPrecio} data-testid="precio-input" />
 *
 * - Recibe `value` como número (o string vacía).
 * - Emite `onValueChange(number)` cada vez que cambia.
 * - Formatea con puntos mientras escribes: "50000" → "50.000".
 * - Ignora todo lo que no sea dígito (no permite negativos ni decimales; para
 *   apps en pesos colombianos donde los montos son enteros).
 */
const MoneyInput = forwardRef(function MoneyInput(
  { value, onValueChange, onChange, className = "", placeholder = "0", onFocus, ...rest },
  ref,
) {
  const display =
    value === "" || value === null || value === undefined
      ? ""
      : Number(value).toLocaleString("es-CO");

  const handleChange = useCallback(
    (e) => {
      let raw = String(e.target.value || "").replace(/\D/g, "");
      // Feb/2026: guard contra entradas absurdas (más de 12 dígitos = > 999 mil
      // millones COP). Protege contra el bug donde el usuario en móvil tipea
      // sin seleccionar el valor previo y los dígitos se concatenan al que
      // ya estaba, generando precios de 10^20 que explotan la UI.
      if (raw.length > 12) raw = raw.slice(0, 12);
      const n = raw === "" ? "" : parseInt(raw, 10);
      if (onChange) onChange({ ...e, target: { ...e.target, value: n } });
      if (onValueChange) onValueChange(n === "" ? "" : n);
    },
    [onChange, onValueChange],
  );

  // Feb/2026: al tocar/enfocar el input, auto-seleccionamos todo el valor
  // para que al tipear SE REEMPLACE (no se concatene). En móvil esto es
  // crítico: el teclado numérico no respeta "select-all" automáticamente.
  const handleFocus = useCallback(
    (e) => {
      try { e.target.select(); } catch {}
      if (onFocus) onFocus(e);
    },
    [onFocus],
  );

  return (
    <Input
      ref={ref}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      value={display}
      onChange={handleChange}
      onFocus={handleFocus}
      placeholder={placeholder}
      className={`tabular-nums ${className}`}
      {...rest}
    />
  );
});

export default MoneyInput;
